/**
 * API Keys Service
 *
 * Type-safe service using @monorepo/shared schemas.
 */
import {
  type ApiKeyResponse,
  ApiKeyResponseSchema,
  type CreateApiKey,
  PaginatedApiKeysResponseSchema,
  type RotateApiKeyResponse,
  RotateApiKeyResponseSchema,
} from '@monorepo/shared/schemas/api-keys'
import { get, post } from '@/lib/api-client'

export const apiKeysService = {
  /**
   * Get the single active API key for the current environment.
   * Returns null if no active key exists (caller should show "Generate" UI).
   */
  async getActiveApiKey(): Promise<ApiKeyResponse | null> {
    const queryParams = new URLSearchParams()
    queryParams.append('status', 'active')
    queryParams.append('limit', '1')

    const result = await get(
      `/api-keys?${queryParams.toString()}`,
      PaginatedApiKeysResponseSchema
    )

    if (result.data.length > 0) {
      return result.data[0]
    }

    return null
  },

  /**
   * Create a new API key. Full `key` value is present in the response.
   */
  async createApiKey(data: CreateApiKey): Promise<ApiKeyResponse> {
    return post('/api-keys', data, ApiKeyResponseSchema)
  },

  /**
   * Rotate the current API key.
   * Returns { id, new_key, key_prefix, rotated_at } — caller merges into state.
   */
  async rotateApiKey(id: string): Promise<RotateApiKeyResponse> {
    return post(`/api-keys/${id}/rotate`, undefined, RotateApiKeyResponseSchema)
  },
}
